import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import StandardReports from '../../StandardReports';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  mapSelectedUsers,
  mergeApiDataIntoConfig,
  reportApiMap,
} from '../../common/ReportHelper';
import {
  useExportUserLoginActivity,
  useGetReportGridDataQuery,
  useLeftPanelReport,
  useSaveUserLoginActivity,
} from '../../../../../hooks/reporting/useReporting';
import {
  IUserLoginActivitySavePayload,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import {
  ReviewLoginExportExcelRequest,
  UserLoginActivityJsonReqPayload,
  UserLoginActivityJsonResponse,
  UserLoginActivitySaveRequestPayload,
  UserLoginActivitySaveResponse,
} from '../../../../../api/types/reportingApi.type';
import {ReportTypeKey, ToastType} from '../../../../../shared/constants';
import {GridSortModel} from '@mui/x-data-grid';
import {column} from './column';
import {currentLoggedInColumn} from './column-currentLoggedIn';
import {ISelectItem} from '../../../../../shared/types';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {t} from 'i18next';

interface ReportConfig {
  infoModalTitle?: string;
  infoContent?: string | string[];
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

const CURRENT_LOGGED_IN_OPTION: ISelectItem = {
  value: 'currentLoggedIn',
  label: 'Currently Logged-In Users',
};

const LOGIN_HISTORY_OPTION: ISelectItem = {
  value: ReportTypeKey.LOGIN_ACTIVITY,
  label: 'Login History',
};

const DEFAULT_PAGE_SIZE = 50;

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}

const StandardReportsContainer: React.FC = () => {
  const location = useLocation();
  const hasFetchedRef = useRef(false);

  const [resolvedReportParams, setResolvedReportParams] = useState<
    {fromDateTime?: any; toDateTime?: any; selectedReviewers?: any} | undefined
  >(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const {item} = (location.state as any) ?? {};
  const reportKeyFromPath = location.pathname.split('/')[2];
  const viewKeyFromPath = location.pathname.split('/')[3];

  const isUserLoginActivityReportRoute = useMemo(() => {
    return reportKeyFromPath === 'user-login-activity';
  }, [reportKeyFromPath]);

  const isUserLoginActivityViewRoute = useMemo(() => {
    return (
      reportKeyFromPath === 'user-login-activity' && viewKeyFromPath === 'view'
    );
  }, [reportKeyFromPath, viewKeyFromPath]);

  const reportIdFromUrl = useMemo(() => {
    if (!isUserLoginActivityViewRoute) return 0;
    const params = new URLSearchParams(location.search);
    const id = params.get('ReportID') ?? '';
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [location.search, isUserLoginActivityViewRoute]);

  useEffect(() => {
    if (!reportIdFromUrl) return;
    hasFetchedRef.current = false;
  }, [reportIdFromUrl, location.key]);

  const reportLabelFromUrl = useMemo(() => {
    if (!isUserLoginActivityViewRoute) return '';
    const params = new URLSearchParams(location.search);
    const raw = params.get('ReportLabel') ?? '';
    const normalized =
      typeof raw === 'string' ? raw.replace(/\+/g, ' ').trim() : '';
    try {
      return normalized ? decodeURIComponent(normalized) : '';
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return normalized;
    }
  }, [location.search, isUserLoginActivityViewRoute]);

  const resolvedReportKey = useMemo(() => {
    if (reportKeyFromPath === 'user-login-activity')
      return 'UserLoginActivityReport';
    return reportKeyFromPath;
  }, [reportKeyFromPath]);

  const leftPanelReportId = useMemo(() => {
    return isUserLoginActivityViewRoute && reportIdFromUrl > 0
      ? reportIdFromUrl
      : (undefined as any);
  }, [isUserLoginActivityViewRoute, reportIdFromUrl]);

  const {data: leftPanelReportData} = useLeftPanelReport(leftPanelReportId);

  const leftPanelResolvedReport = useMemo(() => {
    return (
      (leftPanelReportData as any)?.item ??
      (leftPanelReportData as any)?.report ??
      (leftPanelReportData as any)?.reportModel ??
      (leftPanelReportData as any)?.data ??
      leftPanelReportData ??
      null
    );
  }, [leftPanelReportData]);

  const effectiveReportContext = useMemo(() => {
    return item ?? leftPanelResolvedReport ?? null;
  }, [item, leftPanelResolvedReport]);

  const resolvedReportTypeCode = useMemo(() => {
    return (
      (effectiveReportContext as any)?.reportTypeCode ??
      (effectiveReportContext as any)?.reportType ??
      (effectiveReportContext as any)?.reportTypeCodeName ??
      ''
    );
  }, [effectiveReportContext]);

  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [gridData, setGridData] =
    useState<UserLoginActivityJsonResponse | null>(null);
  const [columns, setColumns] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<ISelectItem | null>(
    CURRENT_LOGGED_IN_OPTION
  );
  const [saveApiCallResponse, setSaveApiCallResponse] =
    useState<UserLoginActivitySaveResponse>({
      notificationId: 0,
      reportId: 0,
    });
  const [saveReportPayload, setSaveReportPayload] =
    useState<IUserLoginActivitySavePayload>({
      reportTypeCode: '',
      selectedReviewers: [],
      fromDateTime: '',
      toDateTime: '',
      showCurrentlyLoggedInUsers: 0,
      selectedCustomColumns: [],
      selectedSource: '',
      pageNumber: 0,
      pageSize: 0,
      sortColumn: '',
      sortDirection: '',
      reportId: 0,
    });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [isApplyClicked, setIsApplyClicked] = useState(false);
  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      sort: [{field: 'userEmail', sort: 'asc'}],
    });

  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);

  const {mutateAsync: saveUserLoginActivity} = useSaveUserLoginActivity();
  const {mutateAsync: getReportGridData} = useGetReportGridDataQuery();
  const {mutateAsync: exportUserLoginActivity} = useExportUserLoginActivity();

  const didInitOptionRef = useRef(false);
  const didInitSelectedCustomColumnsRef = useRef(false);
  const prevSelectedOptionValueRef = useRef<string | number | undefined>(
    undefined
  );
  const didToggleLoginHistory7Ref = useRef(false);

  const allowUserLoginActivityRun = useMemo(() => {
    return (
      !isUserLoginActivityReportRoute ||
      isUserLoginActivityViewRoute ||
      isApplyClicked
    );
  }, [
    isUserLoginActivityReportRoute,
    isUserLoginActivityViewRoute,
    isApplyClicked,
  ]);

  const resolvedViewTitle = useMemo(() => {
    if (!isUserLoginActivityViewRoute) return '';
    const raw = item?.label ?? reportLabelFromUrl;
    const normalized = typeof raw === 'string' ? raw.trim() : raw;
    return normalized ? raw : 'User Login Activity';
  }, [isUserLoginActivityViewRoute, item, reportLabelFromUrl]);

  const updatedConfig = useMemo(
    () =>
      dynamicData && reportConfig?.config
        ? mergeApiDataIntoConfig(reportConfig?.config, dynamicData)
        : reportConfig?.config,
    [reportConfig, dynamicData]
  );

  const extractSelectedCustomColumns = useCallback((report: any) => {
    const candidates = [
      report?.selectedCustomColumns,
      report?.criteria?.selectedCustomColumns,
      report?.reportCriteria?.selectedCustomColumns,
      report?.reportParams?.selectedCustomColumns,
      report?.payload?.selectedCustomColumns,
      report?.filters?.selectedCustomColumns,
    ];
    const found = candidates.find((x) => Array.isArray(x));
    return Array.isArray(found) ? found : [];
  }, []);

  const normalizeSelectedCustomColumns = useCallback(
    (raw: any, optionValue?: string) => {
      const base = Array.isArray(raw) ? raw : [];
      const nums = base
        .map((x: any) => Number(x))
        .filter((n: any) => Number.isFinite(n))
        .map((n: number) => Math.trunc(n));
      let next = Array.from(new Set(nums));
      if (!next.includes(0)) next.unshift(0);
      if (optionValue === 'currentLoggedIn') {
        if (!next.includes(7)) next.push(7);
      }
      if (
        optionValue === 'loginHistory' &&
        !didToggleLoginHistory7Ref.current
      ) {
        next = next.filter((id) => id !== 7);
      }
      return Array.from(new Set(next));
    },
    []
  );

  const applyColumnVisibility = useCallback(
    (baseColumns: any[], hiddenIndexes: number[]) => {
      const hiddenSet = new Set(hiddenIndexes ?? []);
      return (baseColumns ?? []).map((col: any) => {
        const idx = col?.index;
        if (typeof idx === 'number' && hiddenSet.has(idx)) {
          return {...col, isVisible: false};
        }
        return col;
      });
    },
    []
  );

  const effectiveSelectedCustomColumns = useMemo(() => {
    const fromLeftPanel = extractSelectedCustomColumns(leftPanelResolvedReport);
    const raw =
      Array.isArray(saveReportPayload.selectedCustomColumns) &&
      saveReportPayload.selectedCustomColumns.length > 0
        ? saveReportPayload.selectedCustomColumns
        : fromLeftPanel;
    return normalizeSelectedCustomColumns(raw, selectedOption?.value as any);
  }, [
    extractSelectedCustomColumns,
    leftPanelResolvedReport,
    saveReportPayload.selectedCustomColumns,
    normalizeSelectedCustomColumns,
    selectedOption,
  ]);

  const lastSelectedCustomColumnsRef = useRef<number[]>([]);

  useEffect(() => {
    if (effectiveSelectedCustomColumns?.length) {
      lastSelectedCustomColumnsRef.current = effectiveSelectedCustomColumns;
    }
  }, [effectiveSelectedCustomColumns]);

  const triggerToast = useCallback((message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  }, []);

  const [dataAsOf, setDataAsOf] = useState<string>(() => {
    if (leftPanelResolvedReport?.dataAsOf) {
      return dayjs
        .utc(leftPanelResolvedReport.dataAsOf)
        .format('YYYY/MM/DD HH:mm:ss');
    }
    if (gridData?.dataAsOf) {
      return dayjs.utc(gridData.dataAsOf).format('YYYY/MM/DD HH:mm:ss');
    }
    return dayjs.utc().format('YYYY/MM/DD HH:mm:ss');
  });

  const handleExportClick = async () => {
    const selectedCustomColumnsForExport = lastSelectedCustomColumnsRef.current;
    const newColumns =
      selectedOption?.value === 'loginHistory' ? column : currentLoggedInColumn;
    const baseColumns =
      selectedOption?.value === 'loginHistory'
        ? [...newColumns].map((c: any) =>
            c?.index === 7 ? {...c, isVisible: true} : c
          )
        : [...newColumns];
    setColumns(
      applyColumnVisibility(baseColumns, selectedCustomColumnsForExport)
    );
    const exportPayload: ReviewLoginExportExcelRequest = {
      progressReportData: {
        include_Assignment_Detail: 0,
        reportID: saveReportPayload?.reportId ?? 0,
        documentID: 0,
        selected_SearchID: [],
        selected_ActionTypeID: [],
        saveSearchID: 0,
        reportTypeCode: resolvedReportTypeCode ?? '',
        showDailyReport: false,
        fromDateTime: saveReportPayload?.fromDateTime ?? '',
        toDateTime: saveReportPayload?.toDateTime ?? '',
        timeOffset: 0,
        selectedReviewers: saveReportPayload?.selectedReviewers ?? [],
        selectedAssignments: [],
        selectedCustodians: [],
        selectedFoldersOrGroups: [],
        selectedAssignmentClassification: [],
        selectedTagClassification: [],
        selectedTags: [],
        searchTermReportType: '',
        pageNumber: paginationSortConfigRef.current?.pageNumber ?? -1,
        pageSize: paginationSortConfigRef.current?.pageSize ?? -1,
        isAscending:
          (paginationSortConfigRef.current?.sort?.[0]?.sort ?? 'asc') === 'asc'
            ? 'ASC'
            : 'DESC',
        sortingColumn:
          paginationSortConfigRef.current?.sort?.[0]?.field ?? 'USERID',
        rowNumber: 0,
        assignmentFlag: false,
        selected_UserID: [],
        selectedSecurityGroups: [],
        selectedDocumentTypes: [],
        selectedRedactionTags: [],
        selectedCommentTypes: [],
        selectedProductions: [],
        isBulkAction: 0,
        selectedSource: '',
        selectedSourceID: [],
        selectedCustomColumns: selectedCustomColumnsForExport ?? [],
        selectedCustomColumnName: [],
        selectedReviewAssignments: [],
        selectedQCAssignment: [],
        displayBy: 0,
        documentsNotinAnyAssignment: false,
        exportFields: [],
        exportData: [],
        style: '',
        field: '',
        text: '',
        dateStyle: '',
        reportName: '',
        docID: '',
        dataAsOf: gridData?.dataAsOf
          ? dayjs(gridData.dataAsOf).toISOString()
          : dayjs().toISOString(),
        bulkActionSelectionCriteria: [],
        hugeSearchId: '',
        batchLevel: '',
        filterDocumentGrid: '',
        bulkActionABMAssignment: [],
        bulkActionABMDocument: [],
        navigationSourceContext: '',
        navigationDestinationContext: '',
        isSelectedAll: false,
        subActionType: '',
        showCurrentlyLoggedInUsers:
          selectedOption?.value === 'loginHistory' ? 0 : 1,
        isShared: false,
        isSavedSearchSource: false,
        downloadRequestedOn: dayjs().toISOString(),
      },
      actionType: 'EXPORTREPORT',
    };
    try {
      await exportUserLoginActivity(exportPayload);
      triggerToast(t('reports.common.reportExportSuccess'), [
        ToastType.Success,
      ]);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      triggerToast(t('reports.common.reportExportError'), [ToastType.Error]);
    }
  };

  const displayMessage = `Data as of ${dataAsOf} UTC`;

  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = useMemo(
    () => ({
      title: 'User Login Activity Summary',
      subTitle: displayMessage,
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: handleExportClick,
    }),
    [gridData, dataAsOf, handleExportClick]
  );

  const fetchReportGridDataWithPagination = useCallback(
    async (
      reportId: number,
      sortModel: GridSortModel,
      paginationConfig: {pageNumber: number; pageSize: number}
    ) => {
      if (!allowUserLoginActivityRun) return;
      if (!reportId || reportId <= 0) return;

      paginationSortConfigRef.current = {
        pageSize: paginationConfig.pageSize,
        pageNumber: paginationConfig.pageNumber,
        sort: sortModel,
      };
      setPaginationConfig(paginationSortConfigRef.current);
      const payload: UserLoginActivityJsonReqPayload = {
        reportId,
        pageNumber: paginationConfig.pageNumber,
        sortColumn: sortModel[0]?.field ?? 'userEmail',
        sortDirection: sortModel[0]?.sort ?? 'asc',
      };
      try {
        setLoading(true);
        const gridResponse = await getReportGridData(payload);
        const activities = gridResponse?.userLoginActivities;
        if (!Array.isArray(activities) || activities.length === 0) {
          gridResponse.noDataMessage = t(
            'reports.reviewResults.rightPanel.noResultPostApply'
          );
        }
        setGridData(gridResponse);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
          ToastType.Error,
        ]);
      } finally {
        setLoading(false);
      }
    },
    [allowUserLoginActivityRun, getReportGridData, triggerToast]
  );

  useEffect(() => {
    setIsApplyClicked(false);
  }, [reportKeyFromPath, viewKeyFromPath, location.search]);

  useEffect(() => {
    if (!isUserLoginActivityReportRoute) return;
    setIsApplyClicked(false);
    setGridData(null);
    setSaveApiCallResponse({notificationId: 0, reportId: 0});
  }, [isUserLoginActivityReportRoute, selectedOption?.value]);

  useEffect(() => {
    if (!isUserLoginActivityViewRoute) {
      didInitOptionRef.current = false;
      setIsInitialized(true);
      return;
    }
    if (didInitOptionRef.current) return;
    if (!leftPanelResolvedReport) return;
    const fromDateTime =
      (leftPanelResolvedReport as any)?.fromDateTime ??
      (leftPanelResolvedReport as any)?.criteria?.fromDateTime ??
      '';
    const toDateTime =
      (leftPanelResolvedReport as any)?.toDateTime ??
      (leftPanelResolvedReport as any)?.criteria?.toDateTime ??
      '';
    const selectedReviewers =
      (leftPanelResolvedReport as any)?.selectedReviewers ??
      (leftPanelResolvedReport as any)?.criteria?.selectedReviewers ??
      [];
    const hasDates =
      fromDateTime !== null &&
      toDateTime !== null &&
      String(fromDateTime).trim() !== '' &&
      String(toDateTime).trim() !== '';
    const hasReviewers =
      Array.isArray(selectedReviewers) && selectedReviewers.length > 0;
    setResolvedReportParams(
      hasDates && hasReviewers
        ? {fromDateTime, toDateTime, selectedReviewers}
        : undefined
    );
    setSelectedOption(
      hasDates && hasReviewers ? LOGIN_HISTORY_OPTION : CURRENT_LOGGED_IN_OPTION
    );
    didInitOptionRef.current = true;
    setIsInitialized(true);
  }, [isUserLoginActivityViewRoute, leftPanelResolvedReport, setIsInitialized]);

  useEffect(() => {
    if (!isUserLoginActivityViewRoute) return;
    if (reportIdFromUrl <= 0) return;
    setSaveReportPayload((prev) => {
      if (prev.reportId > 0) return prev;
      return {...prev, reportId: reportIdFromUrl};
    });
  }, [isUserLoginActivityViewRoute, reportIdFromUrl]);

  useEffect(() => {
    if (!isUserLoginActivityViewRoute) return;
    if (!leftPanelResolvedReport) return;
    const fromDateTime =
      (leftPanelResolvedReport as any)?.fromDateTime ??
      (leftPanelResolvedReport as any)?.criteria?.fromDateTime ??
      '';
    const toDateTime =
      (leftPanelResolvedReport as any)?.toDateTime ??
      (leftPanelResolvedReport as any)?.criteria?.toDateTime ??
      '';
    const selectedReviewers =
      (leftPanelResolvedReport as any)?.selectedReviewers ??
      (leftPanelResolvedReport as any)?.criteria?.selectedReviewers ??
      [];
    setSaveReportPayload((prev) => {
      let changed = false;
      const next: any = {...prev};
      if (
        (!next.reportTypeCode || String(next.reportTypeCode).trim() === '') &&
        resolvedReportTypeCode
      ) {
        next.reportTypeCode = resolvedReportTypeCode;
        changed = true;
      }
      if (
        (!Array.isArray(next.selectedReviewers) ||
          next.selectedReviewers.length === 0) &&
        Array.isArray(selectedReviewers) &&
        selectedReviewers.length > 0
      ) {
        next.selectedReviewers = selectedReviewers;
        changed = true;
      }
      if (
        (next.fromDateTime === '' ||
          next.fromDateTime === null ||
          next.fromDateTime === undefined) &&
        fromDateTime
      ) {
        next.fromDateTime = fromDateTime;
        changed = true;
      }
      if (
        (next.toDateTime === '' ||
          next.toDateTime === null ||
          next.toDateTime === undefined) &&
        toDateTime
      ) {
        next.toDateTime = toDateTime;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [
    isUserLoginActivityViewRoute,
    leftPanelResolvedReport,
    resolvedReportTypeCode,
  ]);

  useEffect(() => {
    if (!isUserLoginActivityViewRoute) return;
    if (!reportIdFromUrl || reportIdFromUrl <= 0) return;
    const hasLabel =
      reportLabelFromUrl !== null &&
      reportLabelFromUrl !== undefined &&
      String(reportLabelFromUrl).trim() !== '';
    if (hasLabel) return;
    fetchReportGridDataWithPagination(
      reportIdFromUrl,
      paginationSortConfigRef.current.sort,
      {
        pageNumber: paginationSortConfigRef.current.pageNumber,
        pageSize: paginationSortConfigRef.current.pageSize,
      }
    );
  }, [
    isUserLoginActivityViewRoute,
    reportIdFromUrl,
    reportLabelFromUrl,
    fetchReportGridDataWithPagination,
  ]);

  useEffect(() => {
    if (!isUserLoginActivityViewRoute) {
      didInitSelectedCustomColumnsRef.current = false;
      return;
    }
    if (didInitSelectedCustomColumnsRef.current) return;
    if (!leftPanelResolvedReport) return;
    const raw = extractSelectedCustomColumns(leftPanelResolvedReport);
    if (!Array.isArray(raw) || raw.length === 0) {
      didInitSelectedCustomColumnsRef.current = true;
      return;
    }
    setSaveReportPayload((prev) => {
      if (
        Array.isArray(prev.selectedCustomColumns) &&
        prev.selectedCustomColumns.length > 0
      )
        return prev;
      return {
        ...prev,
        selectedCustomColumns: normalizeSelectedCustomColumns(
          raw,
          selectedOption?.value as any
        ),
      };
    });
    didInitSelectedCustomColumnsRef.current = true;
  }, [
    isUserLoginActivityViewRoute,
    leftPanelResolvedReport,
    extractSelectedCustomColumns,
    normalizeSelectedCustomColumns,
    selectedOption,
  ]);

  useEffect(() => {
    if (!resolvedReportKey) return;
    if (!isInitialized) return;

    const isLoginReport = resolvedReportKey === 'UserLoginActivityReport';
    const isLoginHistory = selectedOption?.value === 'loginHistory';
    const hasLeftPanel = leftPanelResolvedReport != null;

    let jsonFileName = '';

    if (reportIdFromUrl && isLoginHistory && isLoginReport && !hasLeftPanel) {
      jsonFileName = resolvedReportKey;
    } else {
      jsonFileName = isLoginReport
        ? isLoginHistory || !selectedOption
          ? resolvedReportKey
          : `${resolvedReportKey}-${selectedOption.value}`
        : resolvedReportKey;
    }

    const newColumns =
      selectedOption?.value === 'loginHistory' ? column : currentLoggedInColumn;
    const baseColumns =
      selectedOption?.value === 'loginHistory'
        ? [...newColumns].map((c: any) =>
            c?.index === 7 ? {...c, isVisible: true} : c
          )
        : [...newColumns];
    setColumns(
      applyColumnVisibility(baseColumns, effectiveSelectedCustomColumns)
    );
    setLoading(true);
    const prevOption = prevSelectedOptionValueRef.current;
    const currentOption = selectedOption?.value;

    if (currentOption === 'loginHistory' && prevOption !== 'loginHistory') {
      setGridData(null);
    }

    prevSelectedOptionValueRef.current = currentOption;

    const load = async () => {
      try {
        const data = await import(`../../config/${jsonFileName}.json`);
        setReportConfig(data.report);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
          ToastType.Error,
        ]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [
    isInitialized,
    resolvedReportKey,
    selectedOption?.value,
    triggerToast,
    applyColumnVisibility,
    effectiveSelectedCustomColumns,
    leftPanelResolvedReport,
    reportIdFromUrl,
  ]);

  useEffect(() => {
    const apiFn = (reportApiMap as any)[resolvedReportKey];
    if (!apiFn) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiFn();
        setDynamicData(data);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
          ToastType.Error,
        ]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [resolvedReportKey, triggerToast]);

  useEffect(() => {
    if (isUserLoginActivityReportRoute) return;
    if (
      !resolvedReportTypeCode &&
      !(isUserLoginActivityViewRoute && reportIdFromUrl > 0)
    )
      return;
    if (saveReportPayload.reportId > 0) return;
    const init = async () => {
      try {
        setLoading(true);
        const payload: UserLoginActivitySaveRequestPayload = {
          reportTypeCode: resolvedReportTypeCode,
          selectedReviewers: [],
          fromDateTime: null,
          toDateTime: null,
          showCurrentlyLoggedInUsers: 1,
          selectedCustomColumns: [],
          selectedSource: '',
          pageNumber: -1,
          pageSize: -1,
          sortColumn: '',
          sortDirection: 'asc',
        };
        const saveResponse = await saveUserLoginActivity(payload);
        if (saveResponse?.reportId && saveResponse.reportId > 0) {
          setSaveReportPayload({
            ...payload,
            reportId: saveResponse.reportId,
          } as any);
          setSaveApiCallResponse({
            notificationId: saveResponse.notificationId,
            reportId: saveResponse.reportId,
          });
        } else {
          console.error('Save API did not return a valid reportId');
        }
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
          ToastType.Error,
        ]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [
    isUserLoginActivityReportRoute,
    isUserLoginActivityViewRoute,
    reportIdFromUrl,
    resolvedReportTypeCode,
    fetchReportGridDataWithPagination,
    saveUserLoginActivity,
    triggerToast,
    saveReportPayload.reportId,
  ]);

  const resolvedParamsForUI = useMemo(() => {
    if (!resolvedReportParams) return resolvedReportParams;
    if (selectedOption?.value !== 'loginHistory') return resolvedReportParams;
    const details = dynamicData?.userDetails ?? [];
    const ids = Array.isArray(resolvedReportParams.selectedReviewers)
      ? resolvedReportParams.selectedReviewers
      : [];
    const labels = ids
      .map((id: any) => {
        const match =
          details.find((u: any) => u?.userId === id) ??
          details.find((u: any) => u?.id === id);
        return match?.userName ?? null;
      })
      .filter((x: any) => !!x);
    const next = {
      ...resolvedReportParams,
      selectedReviewers: labels,
    };
    return next;
  }, [resolvedReportParams, dynamicData, selectedOption]);

  const handleAction = useCallback(
    async (actionKey: string, values: Record<string, any>) => {
      if (actionKey === 'applyChanges') {
        if (isUserLoginActivityReportRoute) setIsApplyClicked(true);
        setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));

        const selectedUsers = mapSelectedUsers(
          values.users ?? [],
          dynamicData?.userDetails ?? []
        );
        if (selectedOption?.value === 'loginHistory') {
          if (selectedUsers.length === 0)
            return triggerToast('Please select at least one user', [
              ToastType.Error,
            ]);
          if (!values.fromDate || !values.toDate)
            return triggerToast('From Date and To Date are required', [
              ToastType.Error,
            ]);
          if (dayjs(values.fromDate).isAfter(dayjs(values.toDate)))
            return triggerToast('Please select a valid date range', [
              ToastType.Error,
            ]);
        }
        try {
          setGridData(null);
          setLoading(true);
          const savePayload: UserLoginActivitySaveRequestPayload = {
            reportTypeCode: item?.reportTypeCode ?? 'TBD22',
            selectedReviewers: selectedUsers.map((u) => u.userId),
            fromDateTime: dayjs(values.fromDate).format('YYYY/MM/DD'),
            toDateTime: dayjs(values.toDate).format('YYYY/MM/DD'),
            showCurrentlyLoggedInUsers:
              selectedOption?.value === 'loginHistory' ? 0 : 1,
            selectedCustomColumns: effectiveSelectedCustomColumns,
            selectedSource: '',
            pageNumber: -1,
            pageSize: -1,
            sortColumn: '',
            sortDirection: 'asc',
          };
          const saveResponse = await saveUserLoginActivity(savePayload);
          setSaveApiCallResponse({
            notificationId: saveResponse.notificationId,
            reportId: saveResponse.reportId,
          });
          setSaveReportPayload({
            ...savePayload,
            reportId: saveResponse?.reportId ?? 0,
          });
        } catch (error: any) {
          triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
            ToastType.Error,
          ]);
        } finally {
          setLoading(false);
        }
      }
      if (actionKey === 'reset') {
        setGridData(null);
        if (isUserLoginActivityReportRoute) {
          setIsApplyClicked(false);
          setSaveApiCallResponse({notificationId: 0, reportId: 0});
        }
      }
    },
    [
      selectedOption,
      dynamicData,
      saveUserLoginActivity,
      triggerToast,
      effectiveSelectedCustomColumns,
      item,
      isUserLoginActivityReportRoute,
    ]
  );

  const handleColumnToggleAction = async (columnId: string) => {
    setSaveReportPayload((prev) => {
      const targetCol = column.find((col) => col.field === columnId);
      const targetIndex = targetCol?.index;
      if (typeof targetIndex !== 'number') {
        return prev;
      }
      if (selectedOption?.value === 'loginHistory' && targetIndex === 7) {
        didToggleLoginHistory7Ref.current = true;
      }
      const withoutZero = (prev.selectedCustomColumns ?? []).filter(
        (id) => id !== 0
      );
      let next = [...withoutZero];
      if (next.includes(targetIndex)) {
        next = next.filter((id) => id !== targetIndex);
      } else {
        next.push(targetIndex);
      }
      next.unshift(0);
      if (selectedOption?.value === 'currentLoggedIn') {
        if (!next.includes(7)) {
          next.push(7);
        }
      }
      next = Array.from(new Set(next));
      return {
        ...prev,
        selectedCustomColumns: next,
      };
    });
  };

  return (
    <>
      {!reportConfig ? (
        <div
          style={{display: 'flex', justifyContent: 'center', marginTop: '50px'}}
          data-testid={`user-login-activity-container-div`}
        >
          <ArrLoadingSpinner show={loading} overlay={true} isOpen={true} />
        </div>
      ) : (
        <StandardReports
          title={
            isUserLoginActivityViewRoute
              ? resolvedViewTitle
              : reportConfig.title
          }
          showInfoIcon={reportConfig.showInfoIcon}
          infoContent={reportConfig.infoContent}
          infoModalTitle={reportConfig.infoModalTitle}
          reportsConfig={updatedConfig}
          selectedOption={selectedOption}
          onOptionChange={setSelectedOption}
          onAction={handleAction}
          data={gridData}
          getRowId={(row: any) => row.userId}
          isNoData={gridData?.userLoginActivities.length === 0}
          loading={loading}
          columns={columns}
          mapRows={(data: any) =>
            data?.userLoginActivities?.map((docData: any) => ({
              userId: docData?.userId,
              emailId: docData?.emailId,
              firstName: docData?.firstName,
              lastName: docData?.lastName,
              projectLabel: docData?.projectLabel,
              roleLabel: docData?.roleLabel,
              inTime: docData?.inTime,
              outTime: docData?.outTime,
            })) ?? []
          }
          rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
          saveReportConfig={saveReportPayload}
          onColumnToggleAction={handleColumnToggleAction}
          gridConfig={{
            primaryKey: 'userId',
            totalRecords: 0,
            pageSizeOptions: [10, 50, 100, 500],
            itemsPerPage: paginationSortConfigRef.current.pageSize,
            currentPage: paginationSortConfigRef.current.pageNumber,
            sortModel: paginationSortConfigRef.current.sort,
            showPerPageOptions: false,
            onSortModelChange: (value) => {
              if (!allowUserLoginActivityRun) return;
              if (_.isEqual(value, paginationSortConfigRef.current.sort))
                return;
              fetchReportGridDataWithPagination(
                saveReportPayload.reportId,
                value,
                {
                  pageNumber: 1,
                  pageSize: paginationSortConfigRef.current.pageSize,
                }
              );
            },
            setItemsPerPage: (value) => {
              if (!allowUserLoginActivityRun) return;
              fetchReportGridDataWithPagination(
                saveReportPayload.reportId,
                paginationSortConfigRef.current.sort,
                {
                  pageNumber: 1,
                  pageSize: value,
                }
              );
            },
            onPageChange: (pageNumber, pageSize) => {
              if (!allowUserLoginActivityRun) return;
              if (pageNumber !== paginationSortConfigRef.current.pageNumber) {
                fetchReportGridDataWithPagination(
                  saveReportPayload.reportId,
                  paginationSortConfigRef.current.sort,
                  {
                    pageNumber,
                    pageSize:
                      pageSize ?? paginationSortConfigRef.current.pageSize,
                  }
                );
              }
            },
            getRowHeight: () => 'auto',
            entityType: 'entries',
          }}
          resolvedReportParams={resolvedParamsForUI}
        />
      )}
      {saveApiCallResponse?.notificationId > 0 &&
        saveApiCallResponse?.reportId > 0 &&
        allowUserLoginActivityRun && (
          <ArrWaitToCompleteModalWrapper
            key={`${saveApiCallResponse?.notificationId}-${saveApiCallResponse?.reportId}`}
            notificationId={saveApiCallResponse?.notificationId}
            reportId={saveApiCallResponse?.reportId}
            onComplete={(reportId: number) =>
              fetchReportGridDataWithPagination(
                reportId,
                paginationSortConfigRef.current.sort,
                {
                  pageNumber: paginationSortConfigRef.current.pageNumber,
                  pageSize: paginationSortConfigRef.current.pageSize,
                }
              )
            }
          />
        )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default StandardReportsContainer;
